import React, { useEffect, useRef, useState } from "react";
import { Upload, FileText, X } from "lucide-react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { DOC_TYPE_LABELS, formatSize } from "@/lib/docStatus";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLang, translateFr as tfr } from "@/i18n";

const MAX_SIZE = 20 * 1024 * 1024;

export default function UploadDocumentModal({ open, onOpenChange, onUploaded, preselectedPlayerId }) {
  const { tf } = useLang();
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState("");
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("other");
  const [label, setLabel] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPlayerId(preselectedPlayerId || "");
    setFile(null);
    setDocType("other");
    setLabel("");
    setExpiresAt("");
    api.get("/players")
      .then((r) => setPlayers(r.data || []))
      .catch(() => setPlayers([]));
  }, [open, preselectedPlayerId]);

  const pickFile = (f) => {
    if (!f) return;
    if (f.size > MAX_SIZE) {
      toast.error(tf("Fichier trop volumineux (max 20 Mo)"));
      return;
    }
    setFile(f);
  };

  const submit = async () => {
    if (!playerId) { toast.error(tf("Sélectionnez un joueur")); return; }
    if (!file) { toast.error(tf("Sélectionnez un fichier")); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      if (label.trim()) fd.append("label", label.trim());
      if (expiresAt) fd.append("expires_at", expiresAt);
      await api.post(`/players/${playerId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(tf("Document ajouté"));
      onOpenChange(false);
      onUploaded?.();
    } catch (e) {
      if (e?.response?.status !== 402) {
        toast.error(e?.response?.data?.detail || tf("Échec de l'upload"));
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !uploading && onOpenChange(v)}>
      <DialogContent className="max-w-md" data-testid="upload-document-modal">
        <DialogHeader>
          <DialogTitle>{tf("Ajouter un document")}</DialogTitle>
          <DialogDescription>
            {tf("Le document sera rattaché au joueur sélectionné.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{tf("Joueur *")}</label>
            <Select value={playerId} onValueChange={setPlayerId} disabled={!!preselectedPlayerId}>
              <SelectTrigger data-testid="upload-player-select">
                <SelectValue placeholder={tf("Sélectionner un joueur")} />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {players.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`upload-player-option-${p.id}`}>
                    {p.name}{p.club ? ` — ${p.club}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{tf("Fichier *")}</label>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="upload-file-input"
            />
            {file ? (
              <div className="flex items-center gap-2.5 rounded-xl border border-border bg-secondary/40 px-3 py-2.5">
                <FileText className="h-4 w-4 text-info shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" data-testid="upload-file-name">{file.name}</div>
                  <div className="text-[11px] text-muted-foreground">{formatSize(file.size)}</div>
                </div>
                <button
                  onClick={() => setFile(null)}
                  className="h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                  aria-label={tf("Retirer le fichier")}
                  data-testid="upload-file-remove"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => inputRef.current?.click()}
                className={cn(
                  "w-full rounded-xl border border-dashed border-border bg-secondary/20 hover:bg-secondary/50",
                  "px-3 py-6 flex flex-col items-center gap-1.5 transition-colors"
                )}
                data-testid="upload-file-picker"
              >
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{tf("Cliquer pour choisir un fichier (max 20 Mo)")}</span>
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">{tf("Catégorie")}</label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="upload-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DOC_TYPE_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{tf(l)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">{tf("Expiration (optionnel)")}</label>
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="h-9 text-sm"
                data-testid="upload-expiry-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{tf("Libellé (optionnel)")}</label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={tfr("Ex : Contrat 2026-2028")}
              className="h-9 text-sm"
              data-testid="upload-label-input"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={uploading} data-testid="upload-cancel-btn">
              {tf("Annuler")}
            </Button>
            <Button onClick={submit} disabled={uploading || !playerId || !file} data-testid="upload-submit-btn">
              {uploading ? tf("Envoi…") : tf("Ajouter le document")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
